/**
 * authService.js — Firebase Auth
 * --------------------------------
 * Same API surface as the previous localStorage mock so callers are unchanged.
 * Session shape: { uid, name, email, plan, effectivePlan, subscriptionStatus,
 *                  subscriptionRenewsAt, scheduledDowngrade, onboardingComplete }
 *
 * onboardingComplete comes from the backend API (GET /accounts/{uid}) — it is
 * the single source of truth. Firebase only manages identity (uid, email, name).
 */

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from 'firebase/auth';

import { firebaseAuth } from './firebase.js';
import { accountApi } from './accountApi.js';

// ── Session cache ──────────────────────────────────────────────────────────────
// Keeps the same shape as the old mock so router guards and composables are
// unchanged. Updated by onAuthStateChanged on boot and by each auth action.
let _session = null;

// Flag: true while register() is in flight so onAuthStateChanged skips its
// update (the account doesn't exist in the backend yet when Firebase fires).
let _registering = false;

// ── authReady ─────────────────────────────────────────────────────────────────
// Resolves once after Firebase has confirmed auth state on the first boot.
// main.js awaits this before mounting so the router guard always has a valid
// session on first navigation.
let _authReadyResolve;
let _authReadyResolved = false;
export const authReady = new Promise(r => { _authReadyResolve = r; });

// ── Helpers ───────────────────────────────────────────────────────────────────

function _buildSession(firebaseUser, account) {
  const sub = account?.subscription ?? {};
  return {
    uid:                  firebaseUser.uid,
    name:                 firebaseUser.displayName || account?.name || '',
    email:                firebaseUser.email || '',
    plan:                 sub.plan               ?? null,
    effectivePlan:        sub.effectivePlan       ?? null,
    subscriptionStatus:   sub.status              ?? null,
    subscriptionRenewsAt: sub.renewsAt            ?? null,
    scheduledDowngrade:   sub.scheduledDowngrade  ?? null,
    onboardingComplete:   account?.onboardingComplete ?? false,
  };
}

function _resolveAuthReady() {
  if (!_authReadyResolved) {
    _authReadyResolved = true;
    _authReadyResolve();
  }
}

// Map Firebase error codes to the legacy error message keys expected by UI components.
function _mapFirebaseError(e) {
  const map = {
    'auth/email-already-in-use': 'EMAIL_ALREADY_EXISTS',
    'auth/invalid-credential':   'INVALID_CREDENTIALS',
    'auth/user-not-found':       'INVALID_CREDENTIALS',
    'auth/wrong-password':       'INVALID_CREDENTIALS',
    'auth/invalid-email':        'INVALID_CREDENTIALS',
  };
  const mapped = map[e.code];
  return mapped ? new Error(mapped) : e;
}

// ── Auth state listener ────────────────────────────────────────────────────────
// Fires once on app boot (and again on any sign-in / sign-out).
// Responsible for populating _session from Firebase user + backend account data.
onAuthStateChanged(firebaseAuth, async (user) => {
  if (_registering) {
    // register() handles _session directly after creating the backend account.
    // Skipping here avoids a 404 race (account doesn't exist yet when Firebase fires).
    _resolveAuthReady();
    return;
  }

  if (user) {
    try {
      const { account } = await accountApi.getAccount(user.uid);
      _session = _buildSession(user, account);
    } catch {
      // Account not yet in backend (mid-registration race) — use minimal session.
      // The register() function will overwrite _session after createAccount() succeeds.
      _session = {
        uid: user.uid, name: user.displayName || '', email: user.email || '',
        plan: null, effectivePlan: null, subscriptionStatus: null,
        subscriptionRenewsAt: null, scheduledDowngrade: null, onboardingComplete: false,
      };
    }
  } else {
    _session = null;
  }

  _resolveAuthReady();
});

// ── Public API ────────────────────────────────────────────────────────────────

/** Return the current session object or null. Synchronous — reads the cached value. */
export function getSession() {
  return _session;
}

/**
 * Register a new account.
 * @returns {object} session
 * @throws {Error} with message 'EMAIL_ALREADY_EXISTS' if email is taken
 */
export async function register(name, email, password) {
  _registering = true;
  try {
    const cred = await createUserWithEmailAndPassword(firebaseAuth, email, password);
    await updateProfile(cred.user, { displayName: name });
    // Create backend account — this is the source of truth for plan / onboardingComplete
    const { account } = await accountApi.createAccount({ uid: cred.user.uid, email, name });
    _session = _buildSession(cred.user, account);
    return _session;
  } catch (e) {
    throw _mapFirebaseError(e);
  } finally {
    _registering = false;
  }
}

/**
 * Login with email + password.
 * @returns {object} session
 * @throws {Error} with message 'INVALID_CREDENTIALS' if credentials are wrong
 */
export async function login(email, password) {
  try {
    const cred = await signInWithEmailAndPassword(firebaseAuth, email, password);
    // Upsert to keep name / email current in backend (idempotent)
    const { account } = await accountApi.createAccount({
      uid:   cred.user.uid,
      email: cred.user.email,
      name:  cred.user.displayName || '',
    });
    _session = _buildSession(cred.user, account);
    return _session;
  } catch (e) {
    throw _mapFirebaseError(e);
  }
}

/**
 * Sign in with a social provider.
 * @param {'google'} provider  Apple / Facebook can be added later.
 * @returns {object} session
 */
export async function loginWithSocial(provider) {
  const providers = {
    google: new GoogleAuthProvider(),
  };
  const authProvider = providers[provider];
  if (!authProvider) {
    throw new Error(`Social login with '${provider}' is not yet configured.`);
  }
  try {
    const cred = await signInWithPopup(firebaseAuth, authProvider);
    const { account } = await accountApi.createAccount({
      uid:   cred.user.uid,
      email: cred.user.email,
      name:  cred.user.displayName || '',
    });
    _session = _buildSession(cred.user, account);
    return _session;
  } catch (e) {
    throw _mapFirebaseError(e);
  }
}

/** Send a password-reset email via Firebase. */
export async function sendPasswordReset(email) {
  await sendPasswordResetEmail(firebaseAuth, email);
}

/** Verify a password-reset oobCode and return the associated email. */
export async function verifyResetCode(oobCode) {
  return await verifyPasswordResetCode(firebaseAuth, oobCode);
}

/** Complete a password reset using the oobCode and new password. */
export async function confirmPasswordResetCode(oobCode, newPassword) {
  await confirmPasswordReset(firebaseAuth, oobCode, newPassword);
}

/** Sign out and clear the session. */
export async function logout() {
  await signOut(firebaseAuth);
  _session = null;
}

/**
 * Mark onboarding complete in the local session cache.
 * The backend API call is handled in OnboardingPage.vue (accountApi.updateAccount).
 */
export function markOnboardingComplete(uid) {
  if (_session && _session.uid === uid) {
    _session = { ..._session, onboardingComplete: true };
  }
}

/**
 * Clear onboardingComplete in local cache.
 * Called after downgradeToFree — the backend already resets the field there.
 */
export function resetOnboarding(uid) {
  if (_session && _session.uid === uid) {
    _session = { ..._session, onboardingComplete: false };
  }
}

/** Update plan in local session cache. */
export function setPlan(uid, plan) {
  if (_session && _session.uid === uid) {
    _session = { ..._session, plan };
  }
}

/**
 * Persist the API subscription object into the session cache.
 * The API is the single source of truth for effectivePlan.
 */
export function setSubscription(uid, subscription) {
  if (!_session || _session.uid !== uid) return;
  const updated = { ..._session };
  if (subscription.plan)          updated.plan               = subscription.plan;
  if (subscription.effectivePlan) updated.effectivePlan      = subscription.effectivePlan;
  if (subscription.status)        updated.subscriptionStatus = subscription.status;
  updated.subscriptionRenewsAt = subscription.renewsAt        ?? null;
  updated.scheduledDowngrade   = subscription.scheduledDowngrade ?? null;
  _session = updated;
}

// ── Unused stubs (child management is handled entirely by accountApi) ──────────
export function addChild() {}
export function getChildren() { return []; }
export function updateChildName() {}
